import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Snackbar,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useLazyFetchGenericQuery,
  usePostGenericMutation,
} from '../../app/dynamicFormApi';
import FormBlock from './FormBlock';
import { buildPayloadFromSchema } from './payload';
import {
  flattenSchemaFields,
  getMissingRequiredFieldsFromFields,
} from './requiredFieldsValidation';

// Helper to build API configuration from schema route definition
function buildApiConfig(routeOrConfig, apiPrefix = 'api/') {
  // Handle simple string routes (backward compatibility)
  if (typeof routeOrConfig === 'string') {
    const path = routeOrConfig;
    // If path already starts with 'api/' or is absolute, use as-is
    if (
      path.startsWith('api/') ||
      path.startsWith('http') ||
      path.startsWith('/')
    ) {
      return { url: path };
    }
    // Otherwise, add the api prefix (default 'api/')
    return { url: `${apiPrefix}${path}` };
  }

  // Handle advanced route configuration objects
  if (routeOrConfig && typeof routeOrConfig === 'object') {
    const config = { ...routeOrConfig };
    // Apply prefix to URL if needed
    if (
      config.url &&
      !config.url.startsWith('api/') &&
      !config.url.startsWith('http') &&
      !config.url.startsWith('/')
    ) {
      config.url = `${apiPrefix}${config.url}`;
    }
    return config;
  }

  return { url: '' };
}

/**
 * Helper to fetch all pages from a paginated API endpoint
 * Recursively fetches all pages until all data is collected
 * @param {Function} triggerFetch - RTK Query lazy fetch function
 * @param {Object} apiConfig - API configuration object with url and params
 * @param {string} dataKey - Key in response containing the array (e.g., 'projects', 'users')
 * @returns {Promise<Array>} - Combined array of all items from all pages
 */
async function fetchAllPages(triggerFetch, apiConfig, dataKey = null) {
  const allItems = [];

  const extractItems = (response) => {
    if (!response) return null;
    if (Array.isArray(response)) return response;
    if (typeof response !== 'object') return null;

    if (dataKey && Array.isArray(response[dataKey])) {
      return response[dataKey];
    }

    // Try common data keys (include forms/tars/projects for convenience)
    const commonKeys = [
      'data',
      'items',
      'results',
      'records',
      'forms',
      'tars',
      'projects',
    ];
    for (const key of commonKeys) {
      if (Array.isArray(response[key])) return response[key];
    }
    return null;
  };

  // If no dataKey is provided, treat this as a non-paginated fetch.
  // This is important for endpoints like `/api/forms` which may reject `page/pageSize`.
  if (!dataKey) {
    try {
      const response = await triggerFetch(apiConfig).unwrap();
      const items = extractItems(response);
      return Array.isArray(items) ? items : [];
    } catch (error) {
      console.warn('Error fetching data:', error);
      return [];
    }
  }

  // Paginated fetch path (dataKey provided)
  let currentPage = 1;
  let totalPages = 1;
  try {
    do {
      const pageConfig = {
        ...apiConfig,
        params: {
          ...(apiConfig.params || {}),
          page: currentPage,
          pageSize: 100,
        },
      };

      const response = await triggerFetch(pageConfig).unwrap();
      const items = extractItems(response);

      if (Array.isArray(items)) {
        allItems.push(...items);
      }

      if (response && typeof response === 'object') {
        if (response.total && response.pageSize) {
          totalPages = Math.ceil(response.total / response.pageSize);
        } else if (response.totalPages) {
          totalPages = response.totalPages;
        } else if (!Array.isArray(items) || items.length === 0) {
          break;
        }
      }

      currentPage++;
    } while (currentPage <= totalPages);
  } catch (error) {
    console.warn('Error fetching paginated data:', error);
  }

  return allItems;
}

/**
 * DynamicForm now supports an optional top-level schema object:
 * {
 *   api: { fetch: 'relative/get/route', submit: 'relative/push/route' },
 *   blocks: [ /* existing block array *-/ ]
 * }
 * Backwards compatible: passing an array still works (no API integration).
 *
 * Field-level flag: { loadFromAPI: true } will populate the field if the
 * fetched JSON contains a matching key. (Nested mapping not yet deep-merged.)
 */
export default function DynamicForm(props) {
  const {
    autoFetch,
    setFormData,
    formData,
    showSubmit,
    readOnly,
    onSubmit,
    isEditMode,
    recordId,
    onCancel,
    onDelete,
    submitMutation,
    authorId,
  } = props;
  const isArraySchema = Array.isArray(props.schema);
  const apiConfig = !isArraySchema ? props.schema.api : undefined;
  const apiPrefix = !isArraySchema ? props.schema.apiPrefix || 'api/' : 'api/';
  // Memoize blocks so its identity stays stable across re-renders
  const blocks = useMemo(
    () => (isArraySchema ? props.schema : props.schema.blocks || []),
    [isArraySchema, props.schema],
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitState, setSubmitState] = useState({
    status: 'idle',
    message: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveSuccessData, setSaveSuccessData] = useState(null);
  const [dynamicOptions, setDynamicOptions] = useState({}); // map: fieldName -> array/options
  const [dirtyMap, setDirtyMap] = useState({}); // map: fieldName -> boolean
  const fetchedRef = useRef(false);
  // Signature-based guards: store last-run signatures to avoid loops
  const optionsFetchedRef = useRef('');
  const hiddenApiFetchedRef = useRef('');
  const hiddenApiValuesRef = useRef({}); // Stores values set by hiddenApi for re-injection after resets
  // Why: hiddenApi blocks back required payload fields the user never sees (e.g. formId,
  // formVersionNumber). Without this gate, a slow / failed lookup leaks empty values into the
  // POST and the backend correctly rejects with an opaque 400.
  const [hiddenApiState, setHiddenApiState] = useState('idle');
  const hiddenApiBlockStatusRef = useRef(new Map());

  // Fully generic RTK Query hooks - work with any API endpoint dynamically
  const [triggerFetch] = useLazyFetchGenericQuery();
  const [postGeneric, { isLoading: isSubmitting }] = usePostGenericMutation();

  // Helper: recursively collect all field objects from blocks (handles group + repeatableSection)
  const getAllFields = useMemo(() => flattenSchemaFields(blocks), [blocks]);

  // Build stable signatures derived from schema/blocks
  const optionDepsSig = useMemo(() => {
    const arr = [];
    (getAllFields || []).forEach((f) => {
      if (f?.loadFromApiRoute) {
        arr.push([
          f.name,
          f.type,
          f.loadFromApiRoute,
          f.loadFromApiField || '',
        ]);
      }
    });
    return JSON.stringify(arr);
  }, [getAllFields]);

  const hiddenDepsSig = useMemo(() => {
    const arr = (blocks || [])
      .filter((b) => b && b.type === 'hiddenApi' && b.route && b.map)
      .map((b) => ({ r: b.route, f: b.filter || null, m: b.map }));
    return JSON.stringify(arr);
  }, [blocks]);

  // Auto-fetch initial data for fields marked loadFromAPI using schema.api.fetch
  useEffect(() => {
    if (!autoFetch) return;
    const fetchPath = apiConfig?.fetch;
    if (!fetchPath) return;
    if (fetchedRef.current) return; // prevent double fetch (e.g., strict mode)
    fetchedRef.current = true;

    const cfg = buildApiConfig(fetchPath, apiPrefix);
    setLoading(true);
    setError(null);

    triggerFetch(cfg)
      .unwrap()
      .then((data) => {
        if (!data || typeof data !== 'object') return;
        setFormData((prev) => {
          const next = { ...prev };
          blocks.forEach((block) => {
            const fields = block.fields || [];
            fields.forEach((field) => {
              if (
                field &&
                field.loadFromAPI &&
                Object.prototype.hasOwnProperty.call(data, field.name)
              ) {
                next[field.name] = data[field.name];
              }
            });
          });
          return next;
        });
      })
      .catch((e) => {
        console.error('DynamicForm fetch error:', e);
        setError(e.message);
      })
      .finally(() => setLoading(false));
  }, [
    autoFetch,
    apiConfig?.fetch,
    triggerFetch,
    setFormData,
    blocks,
    apiPrefix,
  ]);

  // Per-field option/data fetch based on field.loadFromApiRoute and field.loadFromApiField
  // Enhancements:
  // - Supports dot-path in loadFromApiField (e.g. "data.items.results")
  // - Applies equally to dropdown & autocomplete field types.
  // - Automatically fetches ALL pages if API is paginated to ensure complete autocomplete data
  // - Supports dynamic loadFromApiConfig functions that depend on formData
  useEffect(() => {
    if (!optionDepsSig) return;
    // For fields with function-based loadFromApiConfig, create signature including formData
    // so changes to form values that affect config will trigger re-fetch
    const hasFunctionConfig = (getAllFields || []).some(
      (f) =>
        typeof (f?.loadFromApiConfig || f?.loadFromApiRoute) === 'function',
    );
    const currentSig = hasFunctionConfig
      ? optionDepsSig + JSON.stringify(formData || {})
      : optionDepsSig;
    if (optionsFetchedRef.current === currentSig) return;
    optionsFetchedRef.current = currentSig;
    const controllers = [];
    (getAllFields || []).forEach((field) => {
      if (field?.loadFromApiRoute || field?.loadFromApiConfig) {
        // Support both simple route strings and advanced config objects
        // Also support functions that return config based on formData
        let routeConfig = field.loadFromApiConfig || field.loadFromApiRoute;

        // If loadFromApiConfig is a function, call it with formData to get dynamic config
        if (typeof routeConfig === 'function') {
          routeConfig = routeConfig(formData);
        }

        const apiConfig = buildApiConfig(routeConfig, apiPrefix);

        // For autocomplete/dropdown fields, fetch all pages to ensure complete data
        const isOptionsField = ['dropdown', 'autocomplete'].includes(
          field.type,
        );

        if (isOptionsField) {
          // Fetch all pages for autocomplete fields
          const dataKey = field.loadFromApiField?.split('.')[0]; // Extract top-level key for pagination
          fetchAllPages(triggerFetch, apiConfig, dataKey)
            .then((allItems) => {
              // Optional client-side filter — a schema-level
              // `filterOptions(item) => boolean` lets a field narrow the
              // fetched list without a backend query-param change (e.g.
              // "CLIN codes starting with 0004").
              const filtered =
                typeof field.filterOptions === 'function'
                  ? allItems.filter(field.filterOptions)
                  : allItems;
              if (filtered.length > 0) {
                setDynamicOptions((prev) => ({
                  ...prev,
                  [field.name]: filtered,
                }));
              }
            })
            .catch((e) => {
              if (e?.name !== 'AbortError')
                console.warn(`Field ${field.name} fetch all pages error:`, e);
            });
        } else {
          // Non-autocomplete fields: single fetch (existing behavior)
          triggerFetch(apiConfig)
            .unwrap()
            .then((json) => {
              const key = field.loadFromApiField;
              let value;
              if (key) {
                // Dot-path resolution
                value = key
                  .split('.')
                  .reduce(
                    (acc, part) =>
                      acc && acc[part] !== undefined ? acc[part] : undefined,
                    json,
                  );
                // Robust fallback: if path resolution failed but the full response is an array, use it directly
                if (value === undefined && Array.isArray(json)) {
                  value = json;
                }
              } else {
                value = json;
              }

              if (Array.isArray(value)) {
                setDynamicOptions((prev) => ({ ...prev, [field.name]: value }));
              }
              if (
                field.loadFromAPI &&
                Object.prototype.hasOwnProperty.call(json, field.name)
              ) {
                setFormData((prev) => ({
                  ...prev,
                  [field.name]: json[field.name],
                }));
              }
            })
            .catch((e) => {
              if (e?.name !== 'AbortError')
                console.warn(`Field ${field.name} fetch error:`, e);
            });
        }
      }
    });
    return () => controllers.forEach((c) => c.abort());
  }, [
    optionDepsSig,
    getAllFields,
    triggerFetch,
    setFormData,
    apiPrefix,
    formData,
  ]);

  // Hidden API mapping blocks: fetch and map values into formData without rendering
  // Now fetches ALL pages if API is paginated to ensure complete data for filtering
  useEffect(() => {
    if (!hiddenDepsSig) return;
    if (hiddenApiFetchedRef.current === hiddenDepsSig) return; // avoid re-run for same config
    hiddenApiFetchedRef.current = hiddenDepsSig;
    const hiddenBlocks = (blocks || []).filter(
      (b) => b && b.type === 'hiddenApi' && b.route && b.map,
    );
    if (!hiddenBlocks.length) return;

    hiddenApiBlockStatusRef.current = new Map(
      hiddenBlocks.map((_, i) => [i, 'pending']),
    );
    setHiddenApiState('pending');

    const updateBlockStatus = (idx, status) => {
      hiddenApiBlockStatusRef.current.set(idx, status);
      const statuses = Array.from(hiddenApiBlockStatusRef.current.values());
      // Worst-status wins: pending > error > empty > resolved.
      if (statuses.includes('pending')) setHiddenApiState('pending');
      else if (statuses.includes('error')) setHiddenApiState('error');
      else if (statuses.includes('empty')) setHiddenApiState('empty');
      else setHiddenApiState('resolved');
    };

    const getByPath = (obj, path) => {
      if (!obj || !path) return undefined;
      if (typeof path !== 'string') return obj[path];
      return path
        .split('.')
        .reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
    };

    const controllers = [];
    hiddenBlocks.forEach((hb, idx) => {
      // Support both simple route strings and advanced config objects
      const routeConfig = hb.routeConfig || hb.route;
      const apiConfig = buildApiConfig(routeConfig, apiPrefix);

      // Fetch all pages to ensure we can find the filtered item even if paginated
      fetchAllPages(triggerFetch, apiConfig)
        .then((allItems) => {
          let source;

          // If we got items, apply filter or use first item
          if (allItems.length > 0) {
            if (hb.filter && typeof hb.filter === 'object') {
              source = allItems.find((item) =>
                Object.entries(hb.filter).every(
                  ([k, v]) => getByPath(item, k) === v,
                ),
              );
            } else {
              source = allItems[0];
            }
          }

          // Map values into formData and cache in ref for re-injection after resets
          if (source && typeof source === 'object') {
            const mapped = {};
            Object.entries(hb.map).forEach(([targetKey, fromPath]) => {
              mapped[targetKey] = getByPath(source, fromPath);
            });
            hiddenApiValuesRef.current = { ...hiddenApiValuesRef.current, ...mapped };
            if (mapped.formId || mapped.formVersionNumber) {
              console.log('[HiddenApi] formId:', mapped.formId, 'formVersionNumber:', mapped.formVersionNumber);
            }
            setFormData((prev) => ({ ...prev, ...mapped }));
            updateBlockStatus(idx, 'resolved');
          } else {
            updateBlockStatus(idx, 'empty');
          }
        })
        .catch((e) => {
          if (e?.name !== 'AbortError') {
            console.warn('Hidden API block error:', e);
            updateBlockStatus(idx, 'error');
          }
        });
    });
    return () => controllers.forEach((c) => c.abort());
  }, [hiddenDepsSig, blocks, triggerFetch, setFormData, apiPrefix]);

  // Re-inject cached hiddenApi values if they've been wiped (e.g. by resetFormData)
  useEffect(() => {
    const cached = hiddenApiValuesRef.current;
    if (!cached || Object.keys(cached).length === 0) return;
    const missing = Object.entries(cached).some(
      ([k, v]) => v != null && v !== '' && (!formData || formData[k] == null || formData[k] === ''),
    );
    if (missing) {
      setFormData((prev) => ({ ...prev, ...cached }));
    }
  }, [formData, setFormData]);

  const touchAllRequired = (forSubmit = true) => {
    const next = { ...dirtyMap };
    // Use getAllFields to properly traverse all nested structures (columnLayout, repeatableSection, groups, etc.)
    getAllFields.forEach((field) => {
      if (field?.required || (forSubmit && field?.requiredToSubmit)) next[field.name] = true;
    });
    setDirtyMap(next);
  };

  // Validate that all required fields have values. Delegates to the shared
  // validator so non-form submit paths (e.g. the deliverables table action)
  // enforce the exact same schema requirements.
  const validateRequiredFields = (forSubmit = true) =>
    getMissingRequiredFieldsFromFields(getAllFields, formData, forSubmit);

  const hiddenApiGateMessage = () => {
    if (hiddenApiState === 'pending')
      return 'Form configuration is still loading. Please wait a moment and try again.';
    if (hiddenApiState === 'empty')
      return "This form's configuration is missing. Contact your administrator.";
    if (hiddenApiState === 'error')
      return "This form's configuration could not be loaded. Refresh the page or contact support if the problem persists.";
    return '';
  };
  const hiddenApiBlocksSubmit =
    hiddenApiState !== 'idle' && hiddenApiState !== 'resolved';

  // Handle Save (without submit) - saves form in draft/pending state without workflow progression
  const handleSave = async () => {
    if (!apiConfig || !apiConfig.submit) return;

    if (hiddenApiBlocksSubmit) {
      setSnackbar({ open: true, message: hiddenApiGateMessage(), severity: 'error' });
      return;
    }

    // Validate required fields before saving. Draft saves only enforce
    // `required: true`; `requiredToSubmit` fields are deferred until submit.
    const missingFields = validateRequiredFields(false);

    if (missingFields.length > 0) {
      // Show error message with list of missing fields
      const fieldList = missingFields.map(f => `• ${f}`).join('\n');
      const errorMsg = `Please complete the following required fields:\n\n${fieldList}`;
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error'
      });

      // Touch all required fields to show validation errors
      touchAllRequired(false);
      return;
    }

    // Save form immediately without triggering workflow submission (shouldSubmit = false)
    await handleSubmitInternal(false, true); // Pass true to indicate this is a Save (not Save & Submit)
  };

  // Handle exit after save - navigate away
  const handleExitAfterSave = () => {
    setSaveDialogOpen(false);
    setSaveSuccessData(null);
    // Call the parent's onCancel or onSubmit callback to trigger navigation
    if (onCancel) {
      onCancel();
    }
  };

  // Handle continue editing after save - just close dialog
  const handleContinueEditing = () => {
    setSaveDialogOpen(false);
    setSaveSuccessData(null);
  };

  // Handle Save & Submit - validates and submits to workflow
  const handleSaveAndSubmit = async () => {
    if (hiddenApiBlocksSubmit) {
      setSnackbar({ open: true, message: hiddenApiGateMessage(), severity: 'error' });
      return;
    }

    // Validate all required fields have values
    const missingFields = validateRequiredFields();

    if (missingFields.length > 0) {
      // Show error message with list of missing fields
      const fieldList = missingFields.map(f => `• ${f}`).join('\n');
      const errorMsg = `Please complete the following required fields before submitting:\n\n${fieldList}`;
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error'
      });

      // Touch all required fields to show validation errors
      touchAllRequired();
      return;
    }

    touchAllRequired();
    await handleSubmitInternal(true);
  };

  // Internal submit handler - supports both create (POST) and edit (PATCH) modes
  const handleSubmitInternal = async (
    shouldSubmit = true,
    isSaveOnly = false,
  ) => {
    if (!apiConfig || !apiConfig.submit) return;

    // Determine HTTP method based on edit mode
    const httpMethod = isEditMode ? 'PATCH' : 'POST';

    // Build submit URL - append recordId for PATCH operations
    const routeConfig = apiConfig.submitConfig || apiConfig.submit;
    let submitConfig = buildApiConfig(routeConfig, apiPrefix);

    if (isEditMode && recordId) {
      // Append recordId to URL for PATCH
      submitConfig.url = `${submitConfig.url}/${recordId}`;
    }

    setSubmitState({ status: 'submitting', message: '' });

    try {
      const payload = buildPayloadFromSchema(props.schema, formData || {});

      // Use generic RTK Query mutation - fully dynamic, works with any endpoint
      const responseJson = await postGeneric({
        ...submitConfig,
        body: payload,
        method: httpMethod,
        headers: {
          'Content-Type': 'application/json',
          ...submitConfig.headers,
        },
      }).unwrap();

      // After successful form submission, patch the linked deliverable's author only if not already set.
      // Derive the correct ID field from the submit route to avoid cross-reference conflicts
      // (e.g. TR response contains both tarId and trId — we need trId).
      const submitRoute = typeof apiConfig?.submit === 'string' ? apiConfig.submit : '';
      const routeToIdField = {
        'trip-reports': 'trId',
        tars: 'tarId',
        ctps: 'ctpId',
        rips: 'ripId',
        pnrs: 'pnrId',
      };
      const idField = routeToIdField[submitRoute];
      const deliverableId = idField ? responseJson?.[idField] : (responseJson?.ctpId || responseJson?.pnrId || responseJson?.ripId || responseJson?.tarId || responseJson?.trId);
      if (authorId && deliverableId) {
        try {
          const currentDeliverable = await triggerFetch({ url: `api/deliverables/${deliverableId}` }).unwrap();
          if (!currentDeliverable?.authorId) {
            await postGeneric({
              url: `api/deliverables/${deliverableId}`,
              method: 'PATCH',
              body: { authorId },
              headers: { 'Content-Type': 'application/json' },
            }).unwrap();
          }
        } catch (authorPatchError) {
          console.warn('[DynamicForm] Failed to set deliverable author:', authorPatchError);
        }
      }

      // After successful form submission, handle file attachments
      // Always call in edit mode (to handle deletions), or in create mode if there are files
      if (isEditMode || (formData && hasNewFiles())) {
        await uploadPendingAttachments(responseJson);
      }

      // Call afterSubmit hook if defined in schema (for workflow creation, etc.)
      if (typeof apiConfig?.afterSubmit === 'function') {
        try {
          await apiConfig.afterSubmit(
            responseJson,
            {
              postApiWorkflows: postGeneric,
              postApiTarsByIdItinerary: postGeneric,
              postApiTarsByIdEstimate: postGeneric,
            },
            shouldSubmit,
          );
        } catch (afterSubmitError) {
          console.error(
            '[DynamicForm] afterSubmit hook error:',
            afterSubmitError,
          );
          // Don't fail the whole submission if afterSubmit fails
        }
      }

      // Call submit endpoint if Save&Submit was clicked (not just Save)
      // This progresses the workflow from Pending to In Internal Review
      if (shouldSubmit && submitMutation) {
        try {
          // Extract the form ID from the response (try common field names)
          // Prioritize recordId for edit mode to ensure we use the correct existing ID
          const formId =
            isEditMode && recordId
              ? recordId
              : responseJson?.trId ||
                responseJson?.ctpId ||
                responseJson?.ripId ||
                responseJson?.pnrId ||
                responseJson?.tarId ||
                responseJson?.id;

          if (formId) {
            // Add delay to ensure database transaction is fully committed
            // Increased to 250ms for better reliability with PATCH operations
            await new Promise((resolve) => setTimeout(resolve, 250));

            // Call the form-specific submit mutation (e.g., usePostApiTarsByIdSubmitMutation)
            // This triggers workflow progression
            const submitResult = await submitMutation({ id: formId }).unwrap();
          }
        } catch (submitError) {
          console.error('[DynamicForm] Submit endpoint error:', submitError);

          // Enhanced error message with troubleshooting info
          let warningMsg = 'Form saved but workflow submission failed';
          if (submitError?.status === 404) {
            warningMsg += `: ${submitError?.data?.details || 'Record not found'}. The form was created successfully but couldn't be submitted to workflow. Please try submitting it manually from the form list.`;
          } else {
            warningMsg += `: ${submitError?.data?.error || submitError.message}`;
          }

          setSubmitState({
            status: 'warning',
            message: warningMsg,
          });
          setSnackbar({ open: true, message: warningMsg, severity: 'warning' });
          onSubmit?.({
            ok: true,
            payload: responseJson,
            isEdit: isEditMode,
            submitError,
          });
          return;
        }
      }

      const successMsg = shouldSubmit
        ? isEditMode
          ? 'Updated and submitted successfully.'
          : 'Saved and submitted successfully.'
        : isEditMode
          ? 'Updated successfully.'
          : 'Saved successfully.';
      setSubmitState({ status: 'success', message: successMsg });
      setSnackbar({ open: true, message: successMsg, severity: 'success' });

      // If this is a Save operation (not Save & Submit), show dialog to ask exit or continue
      if (isSaveOnly) {
        setSaveSuccessData({ payload: responseJson, isEdit: isEditMode });
        setSaveDialogOpen(true);
        // Call onSubmit immediately after save to trigger post-save logic (estimates, itineraries, etc.)
        // Pass skipNavigation flag so forms know not to navigate away yet
        onSubmit?.({
          ok: true,
          payload: responseJson,
          isEdit: isEditMode,
          skipNavigation: true,
        });
      } else {
        // For Save & Submit, add 100ms delay before calling onSubmit to ensure snackbar is visible
        setTimeout(() => {
          onSubmit?.({ ok: true, payload: responseJson, isEdit: isEditMode });
        }, 100);
      }
    } catch (e) {
      console.error(`[DynamicForm] ${httpMethod} error:`, e);
      const errorMsg = e?.data?.error || e.message;
      setSubmitState({ status: 'error', message: errorMsg });
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
      onSubmit?.({ ok: false, error: e, isEdit: isEditMode });
    }
  };

  // Check if there are new files to upload (files without attachmentId)
  const hasNewFiles = () => {
    const fileFields = (getAllFields || []).filter(
      (f) => f?.type === 'fileUpload',
    );
    return fileFields.some((field) => {
      const files = formData?.[field.name];
      if (!files || !Array.isArray(files)) return false;
      // Check if any file doesn't have an attachmentId (meaning it's new)
      return files.some((fileObj) => !fileObj.attachmentId);
    });
  };

  // Legacy handleSubmit for backwards compatibility
  const handleSubmit = async () => {
    touchAllRequired();
    await handleSubmitInternal(true);
  };

  // Upload file attachments after form submission
  const uploadPendingAttachments = async (formResponse) => {
    // Find all fileUpload fields in the schema
    const fileFields = (getAllFields || []).filter(
      (f) => f?.type === 'fileUpload',
    );
    if (fileFields.length === 0) return;

    // Extract objectId from form response (try common patterns)
    const objectId =
      formResponse?.trId ||
      formResponse?.ctpId ||
      formResponse?.ripId ||
      formResponse?.pnrId ||
      formResponse?.tarId ||
      formResponse?.id;

    if (!objectId) {
      return;
    }

    // Check if there are any new files to upload across all fields
    let hasAnyNewFiles = false;
    for (const field of fileFields) {
      const files = formData?.[field.name];
      if (files && Array.isArray(files)) {
        const newFiles = files.filter(
          (fileObj) => !fileObj.attachmentId && fileObj.file,
        );
        if (newFiles.length > 0) {
          hasAnyNewFiles = true;
          break;
        }
      }
    }

    // Only validate creatorContactId if there are new files to upload
    let creatorContactId = null;
    if (hasAnyNewFiles) {
      // Get current user's contactId from formData or props
      // Try various contact fields from different form types:
      // - fromContactId: CTP, RIP, TAR, PNR forms
      // - pocTravelerId: TR forms (Point of Contact)
      // - travelerId: TR forms (fallback)
      // - creatorContactId: explicit creator field
      // - currentUserContactId: passed from props
      creatorContactId =
        formData?.fromContactId ||
        formData?.creatorContactId ||
        formData?.contactId ||
        formData?.travelerId ||
        formData?.pocTravelerId ||
        props.currentUserContactId;

      if (!creatorContactId) {
        return;
      }
    }

    // Process files for each fileUpload field
    for (const field of fileFields) {
      const files = formData?.[field.name];
      const originalFiles = props.initialData?.[field.name];

      const objectType = field.objectType;
      if (!objectType) {
        continue;
      }

      // In edit mode, handle attachment changes
      if (
        isEditMode &&
        originalFiles &&
        Array.isArray(originalFiles) &&
        files &&
        Array.isArray(files)
      ) {
        // Get original attachmentIds
        const originalAttachmentIds = new Set(
          originalFiles
            .filter((f) => f.attachmentId)
            .map((f) => f.attachmentId),
        );

        // Get current attachmentIds
        const currentAttachmentIds = new Set(
          files.filter((f) => f.attachmentId).map((f) => f.attachmentId),
        );

        // Delete attachments that were removed (exist in original but not in current)
        for (const attachmentId of originalAttachmentIds) {
          if (!currentAttachmentIds.has(attachmentId)) {
            try {
              await postGeneric({
                url: `api/attachments/${attachmentId}`,
                method: 'DELETE',
              }).unwrap();
            } catch (error) {
              console.error(
                `[DynamicForm] Failed to delete attachment ${attachmentId}:`,
                error,
              );
              // Continue even if deletion fails
            }
          }
        }
      }

      // Upload only NEW files (those without attachmentId)
      const newFiles =
        files && Array.isArray(files)
          ? files.filter((fileObj) => !fileObj.attachmentId && fileObj.file)
          : [];

      if (newFiles.length > 0) {
        for (const fileObj of newFiles) {
          try {
            const uploadFormData = new FormData();
            uploadFormData.append('objectId', objectId);
            uploadFormData.append('objectType', objectType);
            uploadFormData.append('name', fileObj.name || fileObj.file.name);
            uploadFormData.append('creatorContactId', creatorContactId);

            if (fileObj.description) {
              uploadFormData.append('description', fileObj.description);
            }

            if (field.formSectionId) {
              uploadFormData.append('formSectionId', field.formSectionId);
            }

            uploadFormData.append('file', fileObj.file);

            await postGeneric({
              url: 'api/attachments',
              method: 'POST',
              body: uploadFormData,
              // Don't set Content-Type - browser will set it with boundary for multipart/form-data
            }).unwrap();
          } catch (error) {
            console.error(
              `[DynamicForm] Failed to upload ${fileObj.name}:`,
              error,
            );
            // Continue with other files even if one fails
          }
        }
      }
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {loading && (
        <Alert icon={<CircularProgress size={16} />} severity="info">
          Loading data...
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {(hiddenApiState === 'empty' || hiddenApiState === 'error') && (
        <Alert
          id="dynamic-form-hidden-api-banner"
          severity="error"
          role="alert"
        >
          {hiddenApiGateMessage()}
        </Alert>
      )}
      {/* Group blocks into sections so each variant-h6 heading and its
          following body blocks render inside ONE solid white card —
          instead of every group rendering as its own card. Page-title
          headings (no variant or variant 'h4') and the leading blocks
          before the first section heading render at the page level
          (outside any section card). */}
      {(() => {
        const sections = [];
        let current = null;
        const pushCurrent = () => {
          if (current) {
            sections.push(current);
            current = null;
          }
        };
        blocks.forEach((block, index) => {
          // Any heading starts a new section card — including the page-title
          // heading at the top of the form. Hidden blocks (type='hidden')
          // are rendered at page level since they have no visible content.
          const isSectionHeading = block.type === 'heading';
          const isHidden = block.type === 'hidden';
          if (isSectionHeading) {
            pushCurrent();
            current = { kind: 'section', heading: { block, index }, body: [] };
          } else if (isHidden) {
            sections.push({ kind: 'pageLevel', block, index });
          } else if (current) {
            current.body.push({ block, index });
          } else {
            // Top-level blocks before any heading — render at page level.
            sections.push({ kind: 'pageLevel', block, index });
          }
        });
        pushCurrent();

        const renderInner = ({ block, index }, opts) => (
          <FormBlock
            key={index}
            block={block}
            formData={formData}
            setFormData={setFormData}
            dynamicOptions={dynamicOptions}
            dirtyMap={dirtyMap}
            setDirtyMap={setDirtyMap}
            readOnly={readOnly}
            inSectionCard={opts?.inSectionCard}
          />
        );

        return sections.map((s, sIdx) => {
          if (s.kind === 'pageLevel') {
            return renderInner(s);
          }
          // Sections whose heading is marked `muted` (e.g. fully-
          // autopopulated Contract Details) render with a slightly grey
          // wash so users read them as "look, don't edit" at a glance.
          const isMuted = Boolean(s.heading?.block?.muted);
          return (
            <Paper
              key={`section-${sIdx}`}
              variant="outlined"
              sx={(theme) => ({
                mt: 2,
                borderRadius: 1,
                overflow: 'hidden',
                // Explicit white in light mode so the cards read as crisp
                // white against the grey form wash. Dark mode keeps the
                // theme paper colour.
                backgroundColor: isMuted
                  ? (theme.palette.mode === 'dark'
                      ? theme.palette.action.hover
                      : '#F5F5F5')
                  : (theme.palette.mode === 'dark'
                      ? theme.palette.background.paper
                      : '#FFFFFF'),
              })}
            >
              {/* Section heading: render via FormBlock inside the card. The
                  inSectionCard flag tells FormBlock to drop margin so the
                  heading sits flush at the top of the card. */}
              <Box sx={{ px: 2, pt: 2 }}>
                {renderInner(s.heading, { inSectionCard: true })}
              </Box>
              <Box sx={{ px: 2, pb: 2 }}>
                {s.body.map((b) => renderInner(b, { inSectionCard: true }))}
              </Box>
            </Paper>
          );
        });
      })()}
      {/* Show action buttons for create/edit modes — sticky to the viewport
          bottom so the action row is always reachable on long forms. The
          background + top border separate it visually from the content
          scrolling underneath, and the negative side margins let it extend
          to the full width of the outer form-shell padding. */}
      {apiConfig?.submit && (showSubmit || isEditMode) && !readOnly && (
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={2}
          sx={(theme) => ({
            position: 'sticky',
            bottom: 0,
            mt: 2,
            py: 1.5,
            px: 2,
            mx: -2,
            backgroundColor: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(6px)',
            borderTop: '1px solid',
            borderColor: 'divider',
            zIndex: 5,
            ...theme.applyStyles('dark', {
              backgroundColor: 'rgba(20,20,20,0.92)',
            }),
          })}
        >
          {isEditMode && onDelete ? (
            <Button
              variant="outlined"
              color="error"
              onClick={onDelete}
              disabled={submitState.status === 'submitting' || isSubmitting}
              sx={{ boxShadow: 0, marginRight: 'auto' }}
            >
              Delete
            </Button>
          ) : !isEditMode && onCancel ? (
            <Button
              variant="outlined"
              color="inherit"
              onClick={onCancel}
              disabled={submitState.status === 'submitting' || isSubmitting}
              sx={{ boxShadow: 0, marginRight: 'auto' }}
            >
              Cancel
            </Button>
          ) : null}
          {hiddenApiState === 'pending' && (
            <Box display="flex" alignItems="center" gap={1} aria-live="polite">
              <CircularProgress size={16} />
              <span>Loading form configuration…</span>
            </Box>
          )}
          <Button
            variant="outlined"
            onClick={handleSave}
            disabled={
              submitState.status === 'submitting' ||
              isSubmitting ||
              hiddenApiBlocksSubmit
            }
            aria-describedby={
              hiddenApiState === 'empty' || hiddenApiState === 'error'
                ? 'dynamic-form-hidden-api-banner'
                : undefined
            }
            sx={{ boxShadow: 0 }}
          >
            {submitState.status === 'submitting' || isSubmitting
              ? 'Saving…'
              : 'Save'}
          </Button>
          <Button
            variant="contained"
            name="submit"
            id="submit"
            onClick={handleSaveAndSubmit}
            disabled={
              submitState.status === 'submitting' ||
              isSubmitting ||
              hiddenApiBlocksSubmit
            }
            aria-describedby={
              hiddenApiState === 'empty' || hiddenApiState === 'error'
                ? 'dynamic-form-hidden-api-banner'
                : undefined
            }
            sx={{ boxShadow: 0 }}
          >
            {submitState.status === 'submitting' || isSubmitting
              ? 'Submitting…'
              : 'Save & Submit'}
          </Button>
        </Box>
      )}
      {/* Show back button for view-only mode */}
      {readOnly && onCancel && (
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onCancel}
            sx={{ boxShadow: 0 }}
          >
            Back
          </Button>
        </Box>
      )}

      {/* Save Success Dialog - Exit or Continue Editing */}
      <Dialog open={saveDialogOpen} onClose={handleContinueEditing}>
        <DialogTitle>Form Saved</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your changes have been saved successfully.
            <br />
            <br />
            Would you like to exit or continue editing?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExitAfterSave} color="inherit">
            Exit
          </Button>
          <Button
            onClick={handleContinueEditing}
            color="primary"
            variant="contained"
            autoFocus
          >
            Continue Editing
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            whiteSpace: 'pre-line', // Preserve line breaks for multi-line messages
            maxWidth: '600px'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
